import React, {useCallback, useMemo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {HTMLUtil} from 'yougile-util'
import {IconButton} from '@components/common/base/buttons'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {HelpKey, HelpProps} from './types'

const hasHelp = (name: string): name is HelpKey =>
  !!Lang.msg.help[name as HelpKey]?.html

const useHelp = (name: HelpKey): {title: string; html: string} =>
  useMemo(() => {
    const title = Lang.msg.help[name].title
    const html = HTMLUtil.sanitizeHtml(Lang.msg.help[name].html, {
      allowedTags: ['b', 'ul', 'li', 'br', 'a', 'img'],
      allowedAttributes: {
        img: ['src', 'alt', 'style', 'data-hover'],
        a: ['href', 'target', 'rel']
      }
    })
    return {title, html}
  }, [Lang.msg, name])

const TwTooltip = tw.div`
  help-new
  rounded-8
  p-16
  space-y-4
  w-320
  bg-grey-d40
  text-invert
`

const TwTitle = tw.div`
  text-xs-semibold
`

const TwContent = tw.div`
  help-new__body
  relative
  text-xs-regular
  overflow-auto
  -mr-12
  pr-12
`

const HelpTooltip = ({
  name,
  className
}: {
  name: HelpKey
  className?: string
}) => {
  const {title, html} = useHelp(name)

  return (
    <TwTooltip className={className}>
      {title && <TwTitle>{title}</TwTitle>}
      <WithPerfectScrollbar className="max-h-240 perfect-scrollbar-with-custom-scroll">
        <TwContent dangerouslySetInnerHTML={{__html: html}} />
      </WithPerfectScrollbar>
    </TwTooltip>
  )
}

export const Help = ({
  name,
  iconSize = 'small',
  iconClassName,
  tooltipClassName,
  placement = 'bottom',
  fallbackPlacements
}: HelpProps) => {
  const ref = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)
  const open = useCallback(() => setVisible(true), [setVisible])
  const close = useCallback(() => setVisible(false), [setVisible])

  return hasHelp(name) ? (
    <>
      <IconButton
        ref={ref}
        className={iconClassName}
        icon="IconHelp"
        color="warning"
        size={iconSize}
        onClick={open}
      />
      {visible && (
        <Dropdown
          content={<HelpTooltip name={name} className={tooltipClassName} />}
          reference={ref}
          theme="new-tooltips"
          visible
          onVisibleChange={close}
          placement={placement}
          fallbackPlacements={fallbackPlacements}
        />
      )}
    </>
  ) : null
}
